/**
 * =========================================================================================
 *     DOBLER DATA SOLUTIONS, LLC - PROPRIETARY AND CONFIDENTIAL
 * =========================================================================================
 * The source code contained herein is owned by Dobler Data Solutions, LLC. The source code
 * and all other copyrightable materials related thereto, including without limitation, all
 * text, logos, designs, graphics, other files, and other code, is protected under Title 17
 * of the United States Code. Any use, including any commercial use, distribution,
 * republication, or display of the source code or any other copyrightable materials related
 * thereto is strictly prohibited. Copyright © 2026 Dobler Data Solutions, LLC.
 * ALL RIGHTS RESERVED.
 * =========================================================================================
 *
 *     File Name       :   token.ts
 *     Description     :   Single source of truth for acquiring the API bearer token. In dev-auth
 *                         bypass mode MSAL is never initialized, so calling any MSAL API throws
 *                         "uninitialized_public_client_application"; this helper short-circuits to
 *                         a synthetic "dev-bypass" token (the backend ignores it in bypass mode).
 *     Module          :   005-web-shell
 *     Component Type  :   Helper
 *
 * -----------------------------------------------------------------------------------------
 *     CHANGE HISTORY
 * -----------------------------------------------------------------------------------------
 *     Version     Date            Author              Description
 * -----------------------------------------------------------------------------------------
 *     1.0         2026-06-25      Peter Dobler        Initial creation (fix bypass MSAL crash)
 * -----------------------------------------------------------------------------------------
 *
 *     DEPENDENCIES
 * -----------------------------------------------------------------------------------------
 *     External        :   @azure/msal-browser
 *     Internal        :   msalConfig
 *
 *     EXPORTS
 * -----------------------------------------------------------------------------------------
 *     isDevBypass, getAccessToken
 *
 * =========================================================================================
 */

import { msalInstance } from "./msalConfig.js";

/** The synthetic token sent in dev-bypass mode (the backend's bypass preHandler ignores it). */
const DEV_BYPASS_TOKEN = "dev-bypass";

const SCOPES = [
  (import.meta.env["VITE_ENTRA_SCOPE"] as string | undefined) ??
    `api://${import.meta.env["VITE_ENTRA_CLIENT_ID"] as string}/access_as_user`,
];

/**
 * True when the synthetic dev caller is active (local dev/test only). Evaluated at runtime — not a
 * module constant — so Vite strips it from production builds and tests can stub the env.
 */
export function isDevBypass(): boolean {
  return (
    import.meta.env["VITE_DEV_AUTH_BYPASS"] === "true" &&
    // Gated on DEV (dev server) not the mode NAME, so custom app modes (spec-016 `rp`) keep the
    // same security property: bypass NEVER survives a build.
    (import.meta.env.DEV || import.meta.env.MODE === "test")
  );
}

/**
 * Acquire the API bearer token. In bypass mode returns the synthetic token WITHOUT touching MSAL
 * (MSAL is never initialized when bypassing). Otherwise acquires silently for the active account,
 * falling back to a redirect on silent failure (mid-session expiry).
 */
export async function getAccessToken(): Promise<string> {
  if (isDevBypass()) {
    return DEV_BYPASS_TOKEN;
  }

  const account = msalInstance.getAllAccounts()[0];
  if (!account) {
    // No account — the router guard will redirect to /signin.
    throw new Error("No authenticated account found");
  }

  try {
    const result = await msalInstance.acquireTokenSilent({ scopes: SCOPES, account });
    return result.accessToken;
  } catch {
    await msalInstance.acquireTokenRedirect({ scopes: SCOPES });
    // acquireTokenRedirect navigates away; this line is never reached.
    throw new Error("Token acquisition redirecting");
  }
}
